import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  Optional,
  ViewEncapsulation
} from '@angular/core';

import { MobileAppBridgeService, PlatformService } from '@hdms/shared/util-core';

import { FloatingDirective, FloatingStackingService } from '../floating';

import { SMARTBAR_TRANSLATIONS, SmartbarTranslations } from './smartbar.translations';

@Component({
  selector: 'shrd-uic-smartbar',
  templateUrl: './smartbar.component.html',
  styleUrls: ['./smartbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SmartbarComponent implements AfterViewInit, OnDestroy {
  element: HTMLElement;

  @HostBinding('attr.role') role = 'region';
  @HostBinding('attr.aria-label') ariaLabel: string;

  @HostBinding('class.shrd-uic-smartbar') readonly hostClass = true;
  @HostBinding('class.shrd-uic-smartbar-light')
  @Input()
  isLight = false;

  @Input() containerClasses = '';

  @HostBinding('class.fixed-bottom')
  @Input()
  isFixed = true;

  @Input() restoreAppSmartbarOnDestroy = true;

  private isMobileNativeSmartbarHidden = false;

  constructor(
    private readonly elRef: ElementRef,
    @Optional() private readonly floatingStackingService: FloatingStackingService,
    @Optional() private readonly floatingDirective: FloatingDirective,
    @Optional()
    @Inject(SMARTBAR_TRANSLATIONS)
    private readonly smartbarTranslations: SmartbarTranslations,
    private readonly platformService: PlatformService,
    private readonly mobileBridgeService: MobileAppBridgeService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.element = elRef.nativeElement;

    if (smartbarTranslations) {
      this.ariaLabel = smartbarTranslations.ariaLabel;
    }

    if (this.floatingDirective) {
      this.floatingDirective.isSmartBar = true;
    }
  }

  ngAfterViewInit(): void {
    if (this.platformService.isServer) {
      return;
    }
    /* Register smartbar in floating stacking service for the service to be able to calculate other floating elements offsets correctly */
    if (this.floatingStackingService && this.isFixed) {
      if (!this.floatingDirective) {
        this.floatingStackingService.registerElement({
          nativeElement: this.element,
          stacking: true,
          isSmartbar: true,
          offset: 0
        });
      } else {
        this.floatingStackingService.updateValue(this.element, 'isSmartbar', true);
      }
    }
    if (this.floatingDirective) {
      // Reset isFixed as floatingDirective takes over handling fixed behavior
      setTimeout(() => {
        this.isFixed = false;
        this.floatingDirective.update();
        this.cdr.markForCheck();
      }, 0);
    }

    // auto hide mobile app native smartbar when a smartbar is fixed to bottom of the page
    if (this.isFixed) {
      this.toggleMobileNativeSmartbar(false);
    }
  }

  ngOnDestroy(): void {
    if (this.floatingStackingService && !this.floatingDirective) {
      this.floatingStackingService.deregisterElement(this.element);
    }

    if (this.isMobileNativeSmartbarHidden && this.restoreAppSmartbarOnDestroy) {
      this.toggleMobileNativeSmartbar(true);
    }
  }

  private toggleMobileNativeSmartbar(showNativeSmartbar: boolean): void {
    if (showNativeSmartbar) {
      this.mobileBridgeService.showNativeSmartbar();
      this.isMobileNativeSmartbarHidden = false;
    } else {
      this.mobileBridgeService.hideNativeSmartbar();
      this.isMobileNativeSmartbarHidden = true;
    }
  }
}
